import { onScopeDispose, ref, type Ref, unref } from 'vue'

type SwipeEnabled = boolean | Ref<boolean> | (() => boolean)

interface HorizontalSwipeOptions {
  canSwipeLeft?: SwipeEnabled
  canSwipeRight?: SwipeEnabled
  enabled?: SwipeEnabled
  edgeResistance?: number
  minDistance?: number
  directionRatio?: number
  settleDuration?: number
  onSwipeLeft: () => void
  onSwipeRight: () => void
}

function resolveEnabled(enabled: SwipeEnabled | undefined): boolean {
  if (typeof enabled === 'function')
    return Boolean(enabled())

  return enabled === undefined ? true : Boolean(unref(enabled))
}

/**
 * Mobile horizontal-swipe recognition that leaves vertical page scrolling
 * untouched and suppresses the synthetic click emitted after a completed
 * swipe. Consumers keep ownership of the actual previous/next transition.
 */
export function useHorizontalSwipe(options: HorizontalSwipeOptions) {
  const dragOffsetX = ref(0)
  const isTracking = ref(false)
  const isDragging = ref(false)
  const isSettling = ref(false)
  let startX = 0
  let startY = 0
  let lastX = 0
  let lastY = 0
  let targetWidth = 0
  let suppressClickUntil = 0
  let directionLock: 'horizontal' | 'vertical' | null = null
  let settleTimer: ReturnType<typeof setTimeout> | null = null

  function clearSettleTimer() {
    if (settleTimer) {
      clearTimeout(settleTimer)
      settleTimer = null
    }
  }

  function resetTracking() {
    isTracking.value = false
    isDragging.value = false
    startX = 0
    startY = 0
    lastX = 0
    lastY = 0
    targetWidth = 0
    directionLock = null
  }

  function resetImmediately() {
    clearSettleTimer()
    resetTracking()
    dragOffsetX.value = 0
    isSettling.value = false
  }

  function canSwipe(direction: 'left' | 'right') {
    const option = direction === 'left' ? options.canSwipeLeft : options.canSwipeRight
    return resolveEnabled(option)
  }

  function finishSettling(direction: 'left' | 'right' | null) {
    if (direction === 'left')
      options.onSwipeLeft()
    else if (direction === 'right')
      options.onSwipeRight()

    dragOffsetX.value = 0
    isSettling.value = false
    settleTimer = null
  }

  function settleGesture(direction: 'left' | 'right' | null) {
    const settleDuration = options.settleDuration ?? 260
    const settleOffset = direction === 'left'
      ? -targetWidth
      : direction === 'right'
        ? targetWidth
        : 0

    isTracking.value = false
    isDragging.value = false
    isSettling.value = true
    dragOffsetX.value = settleOffset

    clearSettleTimer()
    settleTimer = setTimeout(() => finishSettling(direction), settleDuration)
  }

  function onTouchStart(event: TouchEvent) {
    if (isSettling.value || !resolveEnabled(options.enabled) || event.touches.length !== 1) {
      if (!isSettling.value)
        resetImmediately()
      return
    }

    const touch = event.touches[0]
    startX = touch.clientX
    startY = touch.clientY
    lastX = touch.clientX
    lastY = touch.clientY
    targetWidth = (event.currentTarget as HTMLElement | null)?.clientWidth || 0
    isTracking.value = true
    isDragging.value = false
    dragOffsetX.value = 0
    directionLock = null
  }

  function onTouchMove(event: TouchEvent) {
    if (!isTracking.value || event.touches.length !== 1)
      return

    const touch = event.touches[0]
    lastX = touch.clientX
    lastY = touch.clientY

    const distanceX = Math.abs(lastX - startX)
    const distanceY = Math.abs(lastY - startY)
    const directionRatio = options.directionRatio ?? 1.15

    if (!directionLock && distanceX >= 8 && distanceX > distanceY * directionRatio)
      directionLock = 'horizontal'
    else if (!directionLock && distanceY >= 8 && distanceY > distanceX * directionRatio)
      directionLock = 'vertical'

    if (directionLock !== 'horizontal')
      return

    const deltaX = lastX - startX
    const direction = deltaX < 0 ? 'left' : 'right'
    const resistance = options.edgeResistance ?? 0.28
    isDragging.value = true
    dragOffsetX.value = canSwipe(direction) ? deltaX : deltaX * resistance
  }

  function onTouchEnd(event: TouchEvent) {
    if (!isTracking.value)
      return

    const touch = event.changedTouches[0]
    const endX = touch?.clientX ?? lastX
    const endY = touch?.clientY ?? lastY
    const deltaX = endX - startX
    const deltaY = endY - startY
    const distanceX = Math.abs(deltaX)
    const distanceY = Math.abs(deltaY)
    const directionRatio = options.directionRatio ?? 1.15
    const responsiveDistance = targetWidth > 0 ? targetWidth * 0.1 : 0
    const minDistance = Math.max(options.minDistance ?? 42, Math.min(responsiveDistance, 72))
    const direction = deltaX < 0 ? 'left' : 'right'
    const shouldSwitch = directionLock === 'horizontal'
      && resolveEnabled(options.enabled)
      && canSwipe(direction)
      && distanceX >= minDistance
      && distanceX > distanceY * directionRatio

    if (directionLock === 'horizontal')
      suppressClickUntil = Date.now() + 450

    if (directionLock === 'horizontal')
      settleGesture(shouldSwitch ? direction : null)
    else
      resetImmediately()
  }

  function onTouchCancel() {
    if (directionLock === 'horizontal') {
      suppressClickUntil = Date.now() + 450
      settleGesture(null)
    }
    else {
      resetImmediately()
    }
  }

  function onClickCapture(event: MouseEvent) {
    if (Date.now() >= suppressClickUntil)
      return

    event.preventDefault()
    event.stopPropagation()
  }

  function triggerSwipe(direction: 'left' | 'right', width?: number) {
    if (isSettling.value || !resolveEnabled(options.enabled) || !canSwipe(direction))
      return false

    if (width && width > 0)
      targetWidth = width

    if (targetWidth <= 0)
      return false

    settleGesture(direction)
    return true
  }

  onScopeDispose(clearSettleTimer)

  return {
    dragOffsetX,
    isDragging,
    isSettling,
    isTracking,
    onClickCapture,
    onTouchCancel,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
    swipeLeft: (width?: number) => triggerSwipe('left', width),
    swipeRight: (width?: number) => triggerSwipe('right', width),
  }
}
